import Strips from './Strips';
import { Box } from '@mui/material';
import BoardCanvas from 'boardCanvas/BoardCanvas';
import { MaterialNames } from 'Constants';
import {
  MaterialPatternString,
  parseMaterialPatternString,
  reversePatternString,
  spacePattern,
  SubBoardPatternString,
  toPatternString,
} from 'Pattern';
import { useRecoilValue } from 'recoil';
import { BoardType, Material, Units } from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import { createAnonymousBoard } from 'boardClasses/BoardFactory';
import StripsAdvanced from 'boardClasses/stripsAdvanced/StripsAdvanced';
import BuildStep from 'designer/BuildStep';
import { settingsAtom } from 'state/recoilState';
import {
  getDisplayLengthInUnits,
  groupBy,
  mapMapEntries,
  sortBy,
  withKerfAndBuffer,
} from 'utils/utils';

function StripsBuildSteps(props: {
  board: BoardBase<BoardType.Strips>;
}): JSX.Element {
  const { board } = props;

  const settings = useRecoilValue(settingsAtom);

  const { planeBuffer } = settings;
  const { flip, numRows, rowHeight, thickness } = board.params;
  const displayLength = getDisplayLengthInUnits(board.units);
  const pattern = parseMaterialPatternString(board.params.pattern);
  const step1ByMaterial = mapMapEntries(
    groupBy(sortBy(pattern, ['size']).reverse(), 'key'),
    (material, fragments) => [
      MaterialNames[material as Material],
      fragments.map((fragment) => displayLength(fragment.size)).join(', '),
    ]
  );
  const step1Pattern = toPatternString(
    spacePattern(
      Array.from(
        groupBy(sortBy(pattern, ['size']).reverse(), 'key').values()
      ).flat()
    )
  );
  const step1Length = withKerfAndBuffer(thickness, numRows, settings);

  const steps = [
    {
      title: 'Cut the strips',
      content: (
        <Box>
          First, cut all the strips that you'll need for the first glue up. Once
          you've cut the strips, send them through the planer on both sides to
          get the exact thickness for each strip.
          <br /> <br />
          For this board you'll need the following strips that are{' '}
          {displayLength(step1Length)} long: <br />
          {step1ByMaterial.map(([material, sizes], index) => (
            <Box key={index}>
              {`${material}: ${sizes}`}
              <br />
            </Box>
          ))}
        </Box>
      ),
      board: createAnonymousBoard(
        Strips,
        {
          pattern: step1Pattern,
          numRows: 1,
          rowHeight: step1Length,
          thickness: 0,
          flip: false,
        },
        Units.IN
      ),
    },
    {
      title: 'First glue up',
      content: `Arrange the strips in the following order and glue them together. Once the glue has dried, send the assembly though the planer on both sides until it is the same thickness as the row height, which is ${displayLength(
        rowHeight
      )} for this board.`,
      board: createAnonymousBoard(
        Strips,
        {
          pattern: board.params.pattern,
          numRows: 1,
          rowHeight: step1Length,
          thickness: 0,
          flip: false,
        },
        Units.IN
      ),
    },
    {
      title: 'Cut horizontal strips',
      content: `Cut strips that are as wide as board's final thickness (${displayLength(
        thickness
      )}) plus the planing buffer that will be planed off in the end (${displayLength(
        settings.planeBuffer
      )}), which is ${displayLength(planeBuffer + thickness)} for this board.`,
      board: createAnonymousBoard(
        StripsAdvanced,
        {
          pattern: new Array(numRows)
            .fill(`${thickness}A 0.25B`)
            .join(' ') as SubBoardPatternString,
          subpatterns: [board.params.pattern, '1' as MaterialPatternString],
          thickness: 0,
        },
        Units.IN
      ),
    },
    {
      title: 'Rotate 90 degrees like a kebab',
      content: `This places the endgrain facing up.`,
      board: createAnonymousBoard(
        StripsAdvanced,
        {
          pattern: new Array(numRows)
            .fill(`${rowHeight}A 0.25B`)
            .join(' ') as SubBoardPatternString,
          subpatterns: [board.params.pattern, '1' as MaterialPatternString],
          thickness: 0,
        },
        Units.IN
      ),
    },
    ...(flip
      ? [
          {
            title: 'Flip every other strip',
            content: ``,
            board: createAnonymousBoard(
              StripsAdvanced,
              {
                pattern: new Array(numRows)
                  .fill(null)
                  .map(
                    (_, index) => `${rowHeight}${index % 2 ? 'B' : 'A'} 0.25C`
                  )
                  .join(' ') as SubBoardPatternString,

                subpatterns: [
                  board.params.pattern,
                  reversePatternString(board.params.pattern),
                  '1' as MaterialPatternString,
                ],
                thickness: 0,
              },
              Units.IN
            ),
          },
        ]
      : []),
    {
      title: 'Final glue up',
      content: `Glue up the strips. When the glue is dry, plane the board on both sides to it's final thickness, ${displayLength(
        thickness
      )} in this case.`,
      board: createAnonymousBoard(Strips, board.params, Units.IN),
    },
  ];

  return (
    <>
      {steps.map((step, index) => (
        <BuildStep
          key={index}
          title={step.title}
          content={step.content}
          preview={
            <BoardCanvas
              dimensions={step.board.getDimensions()}
              renderables={step.board.getRenderables()}
              scaleFactor={24}
            />
          }
        ></BuildStep>
      ))}
    </>
  );
}

export default StripsBuildSteps;
